import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Eye, Lock, ArrowRight, CheckCircle } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import GlossaryAutoLinker from '@/components/glossary/GlossaryAutoLinker';

const AssistantsGovernance: React.FC = () => {
  const principles = [
    {
      icon: Eye,
      title: 'Transparency',
      description: 'Every assistant clearly identifies as AI and explains its limitations to users.',
    },
    {
      icon: Lock,
      title: 'Data Protection',
      description: 'Conversations are encrypted, stored securely, and never used to train external models.',
    },
    {
      icon: FileText,
      title: 'Accuracy First',
      description: 'Assistants are trained to decline questions they cannot answer accurately.',
    },
    {
      icon: Shield,
      title: 'Human Oversight',
      description: 'Human oversight ensures every assistant includes escalation pathways to human experts when needed.',
    },
  ];

  const industries = [
    { name: 'Accounting', slug: 'accounting', available: true },
    { name: 'Legal', slug: 'legal', available: false },
    { name: 'Financial Services', slug: 'financial-services', available: false },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand-navy to-brand-blue py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
                Governance Framework
              </h1>
              <p className="text-xl text-white/90">
                Trust and transparency are at the core of every Advisory Assistant. 
                Explore our governance principles and industry-specific trust charters.
              </p>
            </div>
          </div>
        </section>

        {/* Core Principles */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
              Core Governance Principles
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Our Advisory Assistants are Governed AI Assistants, designed for professional environments and are built on these foundational principles.
            </p>
            
            {/* Definition Box */}
            <div className="max-w-2xl mx-auto mb-12 bg-brand-teal/5 border border-brand-teal/20 rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-2">What is a Governed AI Assistant?</h3>
              <GlossaryAutoLinker>
                <p className="text-muted-foreground text-sm">
                  A managed assistant configured with governance rules, quality assurance testing, and escalation workflows. 
                  Each Governed AI Assistant is deployed to a single website and works across all pages of that site.
                </p>
              </GlossaryAutoLinker>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {principles.map((principle, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-teal/10 rounded-lg mb-4">
                    <principle.icon className="h-6 w-6 text-brand-teal" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-card-foreground">{principle.title}</h3>
                  <GlossaryAutoLinker>
                    <p className="text-muted-foreground">{principle.description}</p>
                  </GlossaryAutoLinker>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industry Trust Charters */}
        <section className="py-16 lg:py-24 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
              Industry Trust Charters
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Each industry has specific governance requirements. Our Trust Charters document 
              the commitments, boundaries, and safeguards for each sector.
            </p>
            <div className="max-w-2xl mx-auto space-y-4">
              {industries.map((industry) => (
                <div 
                  key={industry.slug}
                  className="bg-card border border-border rounded-lg p-6 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <Shield className="h-8 w-8 text-brand-teal" />
                    <div>
                      <h3 className="font-semibold text-card-foreground">{industry.name} Trust Charter</h3>
                      <p className="text-sm text-muted-foreground">
                        {industry.available ? 'Public governance document' : 'Coming soon'}
                      </p>
                    </div>
                  </div>
                  {industry.available ? (
                    <Link to={`/assistants/governance/${industry.slug}`}>
                      <Button variant="outline" size="sm">
                        View Charter
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" size="sm" disabled>
                      Coming Soon
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Commitment Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">
                Our Governance Commitment
              </h2>
              <div className="space-y-4 text-left">
                {[
                  'All assistants are reviewed and approved before going live',
                  'Monthly governance reports are provided to all clients',
                  'Escalation procedures are tested and documented',
                  'Data handling complies with NZ Privacy Act 2020',
                  'Regular audits ensure ongoing compliance',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-muted/50 p-4 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-brand-teal flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20 bg-brand-navy">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Questions About Governance?
            </h2>
            <p className="text-white/80 mb-6">
              We're happy to discuss our governance framework in detail.
            </p>
            <Link to="/assistants/get-assistant">
              <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90">
                Get in Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsGovernance;
